import { Chip } from '@mui/material';
import { SelectOption as Option } from 'components/select/types';
import { Product } from 'models/products';
import { ClipboardEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useProductDataService } from 'services/product-data.service';
import { getChipSxStylingForLevel } from 'styles/utils';
import { BaseAsyncSelect } from '../../BaseAsyncSelect';
import { tbPalette } from 'styles/colors';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { TbIcon } from 'components/Figma';

const MIN_REQUIRED_CHARACTERS = 3;

const mapProductToOption = (product: Product): Option<string> => ({
  id: product.number,
  value: product.number,
  label: `${product.name} (${product.number})`,
});

export function ProductSelect({
  setSelectedProducts,
  selectedProducts,
  placeholderOverride = undefined,
}: {
  selectedProducts: Product[] | null;
  setSelectedProducts: (value: Product[] | null) => void;
  placeholderOverride?: string;
}) {
  const intl = useIntl();
  const { fetchProductDataResponse } = useProductDataService();
  const [unknownIds, setUnknownIds] = useState<string[]>([]);

  const { disabledFilters } = useTransitiveCacheStore();

  const placeholder =
    placeholderOverride ??
    intl.formatMessage({
      id: 'products.search_placeholder',
      defaultMessage: 'Name, Number, EAN',
    });

  const fetchProducts = async (searchTerm: string) => {
    const response = await fetchProductDataResponse(searchTerm);
    return response.data?.list ?? [];
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const inputValue = event.clipboardData.getData('Text');

    let list = inputValue
      .split(/\r?\n/)
      .join(',')
      .split(',')
      .map(item => item.trim());

    if (list.length === 0) list = inputValue.split(/\r?\n/).map(item => item.trim());

    if (list.length > 1) {
      event.preventDefault();

      const promises = [...new Set(list)].map(item =>
        fetchProductDataResponse(item).then(response =>
          response.data?.list?.length === 1
            ? { resolved: response.data.list[0], unresolved: undefined }
            : { resolved: undefined, unresolved: item }
        )
      );

      Promise.all(promises).then(analysedList => {
        const notFounds = analysedList
          .map(item => item.unresolved)
          .filter((item): item is string => item !== undefined)
          .map(item => item.trim())
          .filter(item => item.length > 0);

        const founds = analysedList.map(item => item.resolved).filter((item): item is Product => item !== undefined);
        const dedupedFounds = [...new Map(founds.map(item => [item.number, item])).values()];

        setSelectedProducts(dedupedFounds);
        setUnknownIds(notFounds);
      });

      setUnknownIds([]);
    }
  };

  const label = intl.formatMessage({
    id: 'filter.key.selectedProducts',
    defaultMessage: 'Product Number or Product Name',
  });

  const disableProductsFilter = disabledFilters.includes('prdNumber');

  return (
    <div>
      <div onPaste={handlePaste}>
        <BaseAsyncSelect
          className={`${disableProductsFilter ? 'disabled' : ''}`}
          disabled={disableProductsFilter}
          selectedItems={selectedProducts}
          onSelectionChange={setSelectedProducts}
          fetchItems={fetchProducts}
          getItemId={product => product.number}
          itemToOption={mapProductToOption}
          minSearchCharacters={MIN_REQUIRED_CHARACTERS}
          placeholder={placeholder}
          showConfirmButton={true}
          label={label}
          id="product-filter"
          renderMenuBottomExtra={(searchValue: string, _minChars: number, _resultsCount: number) => (
            <div className="flex flex-col gap-2">
              {/* CSV Hint - show when no products selected and no search active */}
              {(!selectedProducts || selectedProducts.length === 0) &&
                unknownIds.length === 0 &&
                searchValue.length === 0 && (
                  <div className="flex items-center gap-x-2 text-xss text-text-disclaimer">
                    <TbIcon name="InformationOutlined" />
                    <p className="leading-tight">
                      <FormattedMessage
                        id="products.filter.csvlist-hint"
                        defaultMessage="Enter multiple products by pasting a list separated by commas"
                      />
                    </p>
                  </div>
                )}

              {/* Not found products */}
              {unknownIds.length > 0 && (
                <div className="flex items-center gap-x-2 text-xss">
                  <TbIcon name="InformationOutlined" />
                  <span>
                    <FormattedMessage id="products.filter.csvlist-notfound" values={{ not_found: unknownIds.length }} />
                  </span>
                  <div className="flex gap-1 flex-wrap">
                    {unknownIds.slice(0, 3).map(id => (
                      <Chip
                        key={id}
                        label={id}
                        sx={getChipSxStylingForLevel('Default', {
                          fontSize: '10px !important',
                          height: '20px !important',
                          margin: '4px',
                          color: tbPalette.text.inverse,
                          fontWeight: 'bolder',
                        })}
                      />
                    ))}
                    {unknownIds.length > 3 && <span>+{unknownIds.length - 3} more</span>}
                  </div>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
